import { NavigateBefore, NavigateNext } from "@mui/icons-material"
import {
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Sst2Locker } from "shared/sstv2Types"
import { useGetSstv2LockersQuery } from "src/features/partners/partnersApi"
import { SaSstv2LockerDetailDrawer } from "./SaSstv2LockerDetailDrawer"

export const SaSstv2Lockers: FC = () => {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)
  const [count] = useState(20)

  const qLockers = useGetSstv2LockersQuery({ page, count })
  const lockers = qLockers.data?.data || []

  const [previewLocker, setPreviewLocker] =
    useState<Sst2Locker | null>(null)

  const handleRowClick = useCallback((locker: Sst2Locker) => {
    setPreviewLocker(locker)
  }, [])

  return (
    <Stack data-ui="UI-SL2W-234" direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {`${t("lockers")} (${t("page")} ${page})`}
        </Typography>
        {(qLockers.isLoading || qLockers.isFetching) && (
          <CircularProgress size={18} />
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>ID</strong>
              </TableCell>
              <TableCell>
                <strong>UUID</strong>
              </TableCell>
              <TableCell>
                <strong>{t("name")}</strong>
              </TableCell>
              <TableCell>
                <strong>Serial Number</strong>
              </TableCell>
              <TableCell>
                <strong>{t("online")}</strong>
              </TableCell>
              <TableCell>
                <strong>{t("city")}</strong>
              </TableCell>
              <TableCell>
                <strong>{t("doors")}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lockers.map((locker) => (
              <TableRow
                key={locker.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => handleRowClick(locker)}
              >
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {locker.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {locker.uuid}
                  </Typography>
                </TableCell>
                <TableCell>{locker.name || "-"}</TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {locker.serial_number || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: locker.is_online ? "green" : "red",
                    }}
                  />
                </TableCell>
                <TableCell>{locker.city || "-"}</TableCell>
                <TableCell>{locker.doors?.length ?? 0}</TableCell>
              </TableRow>
            ))}
            {!qLockers.isLoading && lockers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No data
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
        >
          <NavigateBefore />
        </IconButton>
        <Typography variant="body2">{`${t("page")} ${page}`}</Typography>
        <IconButton
          size="small"
          onClick={() => setPage((p) => p + 1)}
          disabled={lockers.length < count}
        >
          <NavigateNext />
        </IconButton>
      </Stack>

      {/* Detail Drawer */}
      <SaSstv2LockerDetailDrawer
        locker={previewLocker}
        open={!!previewLocker}
        onClose={() => setPreviewLocker(null)}
        onLockerUpdated={() => qLockers.refetch()}
      />
    </Stack>
  )
}

export default SaSstv2Lockers
